/* eslint-disable */
import React, { useMemo } from "react";
import { Grid, Paper, Rating, Tooltip } from "@mui/material";
import PhoneIcon from "src/icons/Phone";
import { EmailIcon } from "src/icons";
import { TbAward } from "react-icons/tb";
import {
  CurrentInterviewsActions,
  CurrentInterviewsFeedback,
} from "src/pages/CurrentInterviews/components";
import { confirmWrapper } from "src/components/Dialog";
import { CurrentInterviewsListValues } from "src/pages/CurrentInterviews/types";
import { CompletedInterviewsListValues } from "../../types";
import { useRecruiters } from "src/hooks/query/recruiters";
import { useClients } from "src/hooks/query/clients";
import { useRoleUser } from "src/hooks/query/usersByRole";
import { UpdateInterviewStatus } from "src/pages/CurrentInterviews/types";
import { DialogContentProps } from "src/components/Dialog/Dialog";
import EditorInline from "src/components/elements/Editor/Inline";

interface PannaInterviewReportViewProps extends DialogContentProps {
  data?: any;
  currentInterview?: boolean;
  onSave?: (
    data: UpdateInterviewStatus,
    close: (() => void) | undefined
  ) => void;
  sendEmail?: (data: any, close: (() => void) | undefined) => void;
}

const PannaInterviewReportView = function PannaInterviewReportView(
  props: PannaInterviewReportViewProps
) {
  const { close, data, currentInterview, onSave, sendEmail } = props;

  const { recruitersOptions } = useRecruiters();

  const { clientOptions } = useClients();

  const payload = { roleNameList: "ATSInterviewer" };
  const { userByRoleOptions } = useRoleUser(payload);

  const recruiterIdToNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    recruitersOptions.forEach((recruiter) => {
      map[recruiter.value] = recruiter.label;
    });
    return map;
  }, [recruitersOptions]);

  const clientIdToNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    clientOptions.forEach((client) => {
      map[client.value] = client.label;
    });
    return map;
  }, [clientOptions]);

  const interviewerIdToNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    userByRoleOptions.forEach((interviewer) => {
      map[interviewer.value] = interviewer.label;
    });
    return map;
  }, [userByRoleOptions]);

  const handleActionClick = async (data: CurrentInterviewsListValues) => {
    await confirmWrapper("", {
      variant: "",
      hidePrimaryBtn: true,
      hideSecondaryBtn: true,
      maxWidth: "md",
      fullWidth: true,
      showCloseButton: true,
      disableBackdropClickClose: true,
      disableEscapeKeyDownClose: true,
      dialogContent: (
        <CurrentInterviewsActions
          defaultValues={data}
          onSave={onSave}
          sendEmail={sendEmail}
        />
      ),
    });
  };

  const handleFeedbackClick = async (data: CompletedInterviewsListValues) => {
    await confirmWrapper("", {
      variant: "",
      hidePrimaryBtn: true,
      hideSecondaryBtn: true,
      maxWidth: "md",
      fullWidth: true,
      showCloseButton: true,
      disableBackdropClickClose: true,
      disableEscapeKeyDownClose: true,
      dialogContent: (
        <CurrentInterviewsFeedback defaultValues={data} onSave={onSave} />
      ),
    });
  };

  const onProfileClick = (hashKey: string) => {
    const url = `${window.location.origin}/candidate-profile/${hashKey}/info`;
    window.open(url, "_blank");
  };

  const skills = data?.skills ? JSON.parse(data.skills).join(",") : "";

  const formatInterviewMode = (interviewMode: string) => {
    const formattedMode = interviewMode
      .toLowerCase()
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
    return formattedMode.includes("Interview") ? formattedMode : `${formattedMode} Interview`;
  };

  return (
    <Paper>
      <div className="pb-8 text-secondary text-center text-lg font-bold">
        <span>
          {data?.interviewMode && formatInterviewMode(data.interviewMode)}
        </span>
        <span style={{ fontSize: "14px", marginLeft: "2em" }}>
          <a
            rel="noreferrer"
            className="text-primary"
            style={{ textDecoration: "underline", cursor: "pointer" }}
            onClick={() =>
              currentInterview
                ? handleActionClick(data)
                : handleFeedbackClick(data)
            }
          >
            {currentInterview ? "Take an action" : "Feedback"}
          </a>
        </span>
      </div>
      <Grid container spacing={3} className="w-full">
        <Grid item xs={12} md={12} sm={6} lg={4}>
          <div style={{ borderRight: "1px solid black" }}>
            CANDIDATE INFORMATION
          </div>
          &nbsp;
          <Tooltip title="View complete profile">
            <div className="text-md mb-2 ">
              <div
                className="text-primary cursor-pointer"
                onClick={() => {
                  if (data?.hashKey) {
                    onProfileClick(data.hashKey);
                  }
                }}
              >
                {data?.firstName} {data?.lastName}
              </div>
            </div>
          </Tooltip>
          <div className="text-sm mb-2 flex items-center gap-1">
            <PhoneIcon className="h-4 w-4" /> {data?.phoneNumber}
          </div>
          <div className="text-sm mb-2 flex items-center gap-1">
            <EmailIcon className="h-4 w-4" /> {data?.emailAddress}
          </div>
          <div className="text-sm mb-2 flex items-center gap-1">
            <TbAward className="h-5 w-5" /> {data?.jobTitle}
          </div>
        </Grid>
        <Grid item xs={12} md={12} sm={6} lg={4}>
          <div style={{ borderRight: "1px solid black" }}>JOB INFORMATION</div>
          &nbsp;
          <div className="text-md mb-2">
            Client Name {"-"} {clientIdToNameMap[data?.clientId]}
          </div>
          <div className="text-sm mb-2">
            Job ID/Title {"-"} {data?.jobId}
          </div>
          <div className="text-sm mb-2">
            Pay Rate {"-"} {data?.payRate}
          </div>
          <div className="text-sm mb-2">
            Skills {"-"} {skills}
          </div>
        </Grid>
        <Grid item xs={12} md={12} sm={6} lg={4}>
          <div>INTERVIEW INFORMATION</div>
          &nbsp;
          <div className="text-md mb-2">
            Date {"-"} {data?.interviewDate}
          </div>
          <div className="text-md mb-2 truncate" title={formatInterviewMode(data?.interviewMode)}>
            Type {"-"} {formatInterviewMode(data?.interviewMode)}
          </div>
          <div className="text-sm mb-2">
            Recruiter {"-"} {recruiterIdToNameMap[data?.createdBy]}
          </div>
          <div className="text-sm mb-2">
            Interviewer {"-"} {interviewerIdToNameMap[data?.interviewerId]}
          </div>
        </Grid>
      </Grid>
      {(data?.MLivereportURL || data?.oldMLivereportURL) && (
        <div className="mt-4">
          <div className="flex flex-row border-1 border-[#c3c3c3] border-solid rounded-2xl h-[90px]">
            <div
              className="w-[25%] px-[1%] py-[3%] h-full rounded-l-2xl bg-[#f2f2f6] grid grid-cols-2"
              onClick={() => {
                if (data?.MLivereportURL)
                  window.open(data?.MLivereportURL, "_blank");
                else window.open(data?.oldMLivereportURL, "_blank");
              }}
              style={{ cursor: "pointer" }}
            >
              <div className="text-xs">Tech</div>
              <div className="text-xs">
                <Rating
                  size="small"
                  precision={0.5}
                  disabled
                  defaultValue={data?.techSkill || data?.oldTechSkill}
                />
              </div>
              <div className="text-xs">Communication</div>
              <div className="text-xs">
                <Rating
                  size="small"
                  precision={0.5}
                  disabled
                  defaultValue={
                    data?.communicationSkills || data?.oldCommunicationSkills
                  }
                />
              </div>
            </div>
            <div className="w-[75%] p-2 m-auto h-full overflow-y-auto">
              {data?.mliveInterviewComments ? (
                <span dangerouslySetInnerHTML={{ __html: data.mliveInterviewComments }} />
              ) : (
                "No comments"
              )}
            </div>
          </div>
        </div>
      )}
      {data?.pannareportURL || data?.oldPannareportURL ? (
        <div className="mt-4">
          <div className="flex flex-row border-1 border-[#c3c3c3] border-solid rounded-2xl h-[90px]">
            <div
              className="w-[25%] px-[1%] py-[1%] h-full rounded-l-2xl bg-[#f2f2f6] flex flex-col"
              onClick={() => {
                const url = data?.pannareportURL || data?.oldPannareportURL;
                window.open(url, "_blank");
              }}
              style={{ cursor: "pointer" }}
            >
              <div className="text-base m-auto">Paññã Score</div>
              <div className="text-3xl m-auto">
                {data?.score || data?.oldScore || 0}
              </div>
            </div>
            <div className="w-[75%] p-2 m-auto h-full overflow-y-auto">
              {data?.pannaInterviewComments ? (
                <span dangerouslySetInnerHTML={{ __html: data.pannaInterviewComments }} />
              ) : (
                "No comments"
              )}
            </div>
          </div>
        </div>
      ) : (
        ""
      )}
      {data?.interviewComments && (
        <div className="mt-4">
          <div className="flex flex-row border-1 border-[#c3c3c3] border-solid rounded-2xl h-[90px]">
            <div className="w-full p-2 m-auto h-full overflow-y-auto">
              {/* {data?.interviewComments && parse(data?.interviewComments) || "No comments"} */}
              {data?.interviewComments && <EditorInline
              value={data?.interviewComments || 'No comments'}
              disabled
            />}
            </div>
          </div>
        </div>
      )}
    </Paper>
  );
};

PannaInterviewReportView.defaultProps = {};

PannaInterviewReportView.propTypes = {};

export default PannaInterviewReportView;
